import { FC, useRef, useState, useEffect, CSSProperties } from 'react';
import { useInView } from 'motion/react';
import useClientSideMediaQuery from 'hooks/use-client-side-media-query';
import Button from '../Button';
import Icon from 'components/global/Icon/Primary';
import { TypeBodyLarge } from '../Typography';
import { Container } from 'styled/layouts/container';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Swiper as SwiperType } from 'swiper/types';
import { Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import {
  Section,
  CarouselContainer,
  Pagination,
  Thumbnails,
  FullscreenButton,
  Frame,
  Controls,
  ProgressWrapper,
  ProgressBuffer,
  ProgressBar,
  ProgressButton,
  Review,
  Member,
  MemberPill,
} from './review-carousel.styled';
import { VideoProps } from 'components/home/content/review-carousel.content';
import { buttonEventHandler } from 'lib/utils/button-event-handler';
import MuxPlayer from 'components/reusable/MuxPlayer/mux-player';

export interface ReviewProps {
  videos: VideoProps[];
  headline?: React.ReactNode;
}

const ReviewCarousel: FC<ReviewProps> = ({ videos, headline }) => {
  const swiperRef = useRef<SwiperType>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRefs = useRef<any[]>([]);
  const videoWrapperRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isWaiting, setIsWaiting] = useState(false);
  const [videoStates, setVideoStates] = useState<{
    [key: number]: { progress: number; currentTime: number };
  }>({});
  const isMobile = useClientSideMediaQuery(959);
  const isInView = useInView(containerRef, {
    initial: false,
    amount: 0.2,
  });
  const reviewIsInView = useInView(containerRef, {
    initial: false,
    amount: 0.5,
  });

  const progressRef = useRef<HTMLDivElement>(null);
  const bufferRef = useRef<HTMLDivElement>(null);

  const handleThumbnailClick = (index: number) => {
    if (swiperRef.current) {
      if (isPlaying) {
        videoRefs.current[activeIndex]?.pause();
        setIsPlaying(false);
      }
      swiperRef.current.slideToLoop(index);
      buttonEventHandler(
        `Thumbnail click at index ${index}`,
        'Reviews Carousel',
      );
    }
  };

  const handleMuted = () => {
    const muteString = isMuted ? 'Unmute' : 'Mute';
    buttonEventHandler(`${muteString} video`, 'Reviews Carousel');
    setIsMuted(!isMuted);
  };

  const handleFullScreen = () => {
    if (!isFullscreen) {
      const videoElement = videoRefs.current[activeIndex];
      if (videoElement) {
        if (videoElement.requestFullscreen) {
          videoElement.requestFullscreen();
        } else if ((videoElement as any).webkitEnterFullscreen) {
          // iOS Safari specific
          (videoElement as any).webkitEnterFullscreen();
        }
        buttonEventHandler('Enter fullscreen', 'Reviews Carousel');
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if ((document as any).webkitExitFullscreen) {
        (document as any).webkitExitFullscreen();
      }
      buttonEventHandler('Exit fullscreen', 'Reviews Carousel');
    }
  };

  const handlePlayPauseClick = (
    e: React.MouseEvent<HTMLDivElement> | React.MouseEvent<HTMLButtonElement>,
  ) => {
    // chrome likes to default to playing a fullscreen video on click
    // this sends our functionality into a neverending playpause loop
    // preventdefault allows us to keep our desired functionality
    e.preventDefault();
    // The video registers after its first frame loads; until then a tap must not show the pause icon.
    if (!videoRefs.current[activeIndex]) return;
    if (!isPlaying) {
      videoRefs.current[activeIndex]?.play();
      if (isWaiting) setIsWaiting(false);
      setIsPlaying(true);
      buttonEventHandler(
        `Play video at index ${activeIndex}`,
        'Reviews Carousel',
      );
    } else {
      videoRefs.current[activeIndex]?.pause();
      setIsPlaying(false);
      setIsWaiting(false);
      buttonEventHandler(
        `Pause video at index ${activeIndex}`,
        'Reviews Carousel',
      );
    }
  };

  const onProgress = () => {
    const element = videoRefs.current[activeIndex];
    if (!element.buffered || element.buffered.length === 0) return;
    const bufferedEnd = element.buffered.end(element.buffered.length - 1);
    const duration = element.duration;
    if (bufferRef && duration > 0) {
      bufferRef.current!.style.transform = `scaleX(${(bufferedEnd / duration) * 100}%)`;
    }
  };

  const onTimeUpdate = () => {
    setIsWaiting(false);
    const element = videoRefs.current[activeIndex];
    const duration = element.duration;
    const currentTime = element.currentTime;

    setVideoStates((prev) => ({
      ...prev,
      [activeIndex]: {
        progress: (currentTime / duration) * 100,
        currentTime,
      },
    }));

    if (progressRef && duration > 0) {
      progressRef.current!.style.transform = `scaleX(${(currentTime / duration) * 100}%)`;
    }
  };

  useEffect(() => {
    const onFullscreenChange = () => {
      const isNowFullscreen = !!(
        document.fullscreenElement || (document as any).webkitFullscreenElement
      );
      setIsFullscreen(isNowFullscreen);
    };

    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', onFullscreenChange);
    };
  }, []);

  useEffect(() => {
    if (!videoRefs.current) return;
    const element = videoRefs.current[activeIndex];
    if (!element) return;

    const previousState = videoStates[activeIndex];
    if (previousState) {
      element.currentTime = previousState.currentTime;
      if (progressRef.current) {
        progressRef.current.style.transform = `scaleX(${previousState.progress}%)`;
      }
    } else {
      if (progressRef.current) {
        progressRef.current.style.transform = 'scaleX(0)';
      }
      if (bufferRef.current) {
        bufferRef.current.style.transform = 'scaleX(0)';
      }
    }

    element.addEventListener('progress', onProgress);
    element.addEventListener('timeupdate', onTimeUpdate);
    return () => {
      element.removeEventListener('progress', onProgress);
      element.removeEventListener('timeupdate', onTimeUpdate);
    };
  }, [activeIndex]);

  const getMuxPlayerStyle = () => {
    const baseStyle = {
      '--controls': 'none' as const,
    };

    if (isMobile) {
      return {
        ...baseStyle,
        '--media-object-fit': 'cover',
      };
    }

    return {
      ...baseStyle,
    };
  };
  const isDevEnvironment = process.env.NEXT_PUBLIC_STAGING === 'true';

  const getPlaybackId = (video: VideoProps) => {
    if (isMobile) {
      return isDevEnvironment
        ? video.mobileDevPlaybackId
        : video.mobilePlaybackId;
    }

    return isDevEnvironment ? video.devPlaybackId : video.playbackId;
  };

  return (
    <Section>
      {headline && headline}
      <CarouselContainer
        ref={containerRef}
        className={isInView ? 'in-view' : undefined}
      >
        <Swiper
          modules={[Navigation]}
          spaceBetween={12}
          slidesPerView={1.1}
          speed={800}
          breakpoints={{
            960: {
              slidesPerView: 1.5,
            },
          }}
          centeredSlides={true}
          initialSlide={videos.length > 2 ? videos.length / 2 : 0}
          grabCursor={true}
          onSwiper={(swiper) => (swiperRef.current = swiper)}
          onSlideChange={(swiper) => {
            setActiveIndex(swiper.realIndex);

            if (isPlaying) {
              videoRefs.current[activeIndex]?.pause();
              setIsPlaying(false);
            }

            // Reset progress bar and elapsed time
            if (progressRef.current) {
              progressRef.current.style.transform = 'scaleX(0)';
            }
          }}
        >
          {videos.map((video: VideoProps, index: number) => (
            <SwiperSlide key={index}>
              <Frame
                ref={activeIndex == index ? videoWrapperRef : undefined}
                className={`${isFullscreen ? 'video-frame is-fullscreen' : 'video-frame'}`}
                onClick={(e: React.MouseEvent<HTMLDivElement>) =>
                  handlePlayPauseClick(e)
                }
              >
                <FullscreenButton
                  onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                    // handleplaypauseclick needs to be on the frame (parent) but we want to not have fullscreen play/pause
                    e.stopPropagation();
                    handleFullScreen();
                  }}
                  className={activeIndex == index ? 'is-active' : undefined}
                >
                  {isFullscreen ? (
                    <Icon
                      name='close'
                      size='md'
                      className='close'
                      aria-label='Exit fullscreen'
                    />
                  ) : (
                    <Icon
                      name='fullscreen'
                      size='md'
                      className='fullscreen'
                      aria-label='Fullscreen'
                    />
                  )}
                </FullscreenButton>

                {video.review && (
                  <Review
                    className={
                      activeIndex == index && isInView ? 'is-active' : undefined
                    }
                  >
                    "{video.review}"
                  </Review>
                )}

                {video.name && video.memberFrom && (
                  <Member
                    className={
                      activeIndex == index && reviewIsInView
                        ? 'is-active'
                        : undefined
                    }
                  >
                    <TypeBodyLarge color='inverse-primary'>
                      ● {video.name}
                    </TypeBodyLarge>
                    <TypeBodyLarge color='inverse-primary'>
                      Member for <MemberPill>{video.memberFrom}</MemberPill>
                    </TypeBodyLarge>
                  </Member>
                )}
                <MuxPlayer
                  playbackId={getPlaybackId(video)}
                  isManuallyControlled={true}
                  onPause={() => {
                    setIsPlaying(false);
                  }}
                  onPlay={() => {
                    setIsPlaying(true);
                  }}
                  videoRefs={videoRefs}
                  muted={isMuted}
                  height='100%'
                  width='100%'
                  index={index}
                  posterTime={video.posterTime}
                  lazy={false}
                  deferUntilNearView
                  // About 2x the slot width (3x on phones).
                  posterWidth={isMobile ? 1080 : 1600}
                  thumbnailTime={video.posterTime}
                  /* @ts-ignore */
                  style={getMuxPlayerStyle()}
                />
              </Frame>
            </SwiperSlide>
          ))}
        </Swiper>

        {/* Thumbnails */}
        <Container>
          <Pagination className='video-pagination'>
            <svg style={{ display: 'none' }}>
              <defs>
                <filter id='filter'>
                  <feGaussianBlur
                    in='SourceGraphic'
                    stdDeviation='7'
                    result='blur'
                  />
                  <feColorMatrix
                    in='blur'
                    mode='matrix'
                    values='1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 19 -11'
                    result='filter'
                  />
                  <feComposite in='SourceGraphic' in2='filter' />
                </filter>
              </defs>
            </svg>

            {/* Controls */}
            <Controls>
              <Button onClick={handleMuted} size='small' color='seedgreen'>
                {isMuted ? (
                  <Icon name='volume-off' size='md' aria-label='Unmute' />
                ) : (
                  <Icon name='volume-on' size='md' aria-label='Mute' />
                )}
              </Button>
            </Controls>

            <Thumbnails>
              {videos.map((video: VideoProps, index: number) =>
                index == activeIndex ? (
                  <ProgressWrapper key={index}>
                    <ProgressBuffer ref={bufferRef} />
                    <ProgressBar ref={progressRef} />
                    <ProgressButton onClick={(e) => handlePlayPauseClick(e)}>
                      {isPlaying ? (
                        <Icon name='pause' size='md' />
                      ) : (
                        <Icon name='play' size='md' />
                      )}
                      {videos[activeIndex].title}
                    </ProgressButton>
                  </ProgressWrapper>
                ) : (
                  <Frame
                    key={index}
                    style={{ '--ratio': '1 / 1' } as CSSProperties}
                    type='rounded'
                  >
                    <img
                      src={video.thumb}
                      alt={`Thumbnail ${index}`}
                      loading='lazy'
                      decoding='async'
                      onClick={() => handleThumbnailClick(index)}
                    />
                  </Frame>
                ),
              )}
            </Thumbnails>
          </Pagination>
        </Container>
      </CarouselContainer>
    </Section>
  );
};

export default ReviewCarousel;
